import React, { useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertProps,
  AlertTitle,
  Box,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import { SxProps, Theme, alpha, useTheme } from '@mui/material/styles';
import { capitalizeFirstLetter } from '../../utility/functions';
import { ApiErrorResponse } from 'utility/types/dataProvider';
import { resolveSxProps } from 'utility/styles';

export type ErrorDetails = {
  status?: number | string | null | undefined;
  message?: number | string | null | undefined;
  fullDetails?: ApiErrorResponse | Record<string, string> | null | undefined;
  [key: string]: any;
};

export type ErrorAlertsProps = {
  severity?: AlertProps['severity'];
  showSeverity?: boolean; // Whether to show the severity of the alert
  showDivider?: boolean; // Show a divider between the header and the error details
  headerText?: string; // headerText to display in the alert title
  subHeaderText?: string; // subHeaderText to display below the header text
  customNote?: React.ReactNode; // Edge cases can pass custom JSX for more info.
  errors?: ErrorDetails[]; // An array of objects conforming to ErrorDetails
  showDetails?: boolean; // Whether to show the error details - pass full error in details
  buttonOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; // The onClick event for the button (if any)
  buttonText?: string; // The buttonText for the button - must be used with buttonOnClick
  buttonIcon?: React.ReactNode; // The buttonIcon for the button - must be used with buttonOnClick
  buttonDisabled?: boolean; // The buttonDisabled for the button - must be used with buttonOnClick
  sx?: SxProps<Theme>; // sx prop to the root Alert component
  headerSx?: SxProps<Theme>; // sx prop to the header text
  subHeaderSx?: SxProps<Theme>; // sx prop to the sub-header text
};

const ErrorAlerts = ({
  errors = [],
  showDetails = false,
  showSeverity = false,
  showDivider = false,
  headerText = '',
  subHeaderText = '',
  customNote = null,
  severity = 'error',
  buttonOnClick,
  buttonText,
  buttonIcon,
  buttonDisabled,
  sx,
  headerSx,
  subHeaderSx,
}: ErrorAlertsProps) => {
  const theme = useTheme();

  const severityTxt = capitalizeFirstLetter(severity);

  const showActionButton = Boolean(buttonText) && Boolean(buttonOnClick);

  const handleActionButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonOnClick) {
      buttonOnClick(event);
    }
  };

  const rootAlertSx: SxProps<Theme> = useMemo(() => {
    const propsSx = resolveSxProps(sx, theme);
    const finalSx = {
      outline: `1px solid ${theme?.palette?.[severity]?.light}`,
      borderRadius: 1,
      p: 1,
      m: 0.5,
      '& .MuiAlert-action': {
        px: 0,
        py: 0.5,
        mx: 'auto',
        minWidth: 'fit-content',
        '& .MuiButton-startIcon': {
          mr: 0.5,
          p: 0,
        },
      },
      '& .MuiAlertTitle-root': {
        fontSize: 16,
        fontWeight: 500,
        m: 0,
        p: 1,
        border: `1px solid ${alpha(theme?.palette?.[severity]?.main, 0.25)}`,
        backgroundColor: alpha(theme?.palette?.[severity]?.light, 0.25),
        borderRadius: 1,
      },
      '& .MuiAlert-icon': {
        m: 0,
        py: 1,
      },
      '& .MuiAlert-message': {
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'center',
        flexGrow: 1,
        gap: 0.5,
        mx: 1,
        my: 0,
        p: 0,
      },
      ...propsSx,
    };
    return finalSx;
  }, [sx, severity, theme]);

  const finalHeaderSx: SxProps<Theme> = useMemo(() => {
    const propsSx = resolveSxProps(headerSx, theme);
    const finalSx = {
      fontSize: 18,
      fontWeight: 500,
      ...propsSx,
    };
    return finalSx;
  }, [headerSx, theme]);

  const finalSubHeadSx: SxProps<Theme> = useMemo(() => {
    const propsSx = resolveSxProps(subHeaderSx, theme);
    const finalSx = {
      fontSize: 14,
      ...propsSx,
    };
    return finalSx;
  }, [subHeaderSx, theme]);

  return (
    <Alert
      data-testid="error-alert"
      severity={severity}
      sx={rootAlertSx}
      action={
        showActionButton && (
          <Button
            color={severity}
            id="error-alert-button"
            variant="contained"
            size="small"
            disableElevation={true}
            onClick={handleActionButton}
            startIcon={buttonIcon}
            disabled={buttonDisabled}
          >
            {buttonText}
          </Button>
        )
      }
    >
      {showSeverity && (
        <AlertTitle
          data-testid="error-alert-severity"
          sx={{ fontSize: 16 }}
        >
          {severityTxt}
        </AlertTitle>
      )}

      {headerText && (
        <Typography
          data-testid="error-alert-header"
          sx={finalHeaderSx}
        >
          {headerText}
        </Typography>
      )}

      {subHeaderText && (
        <Typography
          data-testid="error-alert-sub-header"
          sx={finalSubHeadSx}
        >
          {subHeaderText}
        </Typography>
      )}

      {customNote && (
        <Box
          data-testid="error-alert-note"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            color: theme.palette.text.primary,
            fontSize: 14,
          }}
        >
          {customNote}
        </Box>
      )}

      {showDivider && (
        <Divider
          data-testid="error-alert-divider"
          sx={{ my: 1 }}
        />
      )}

      {errors.map((error: ErrorDetails, index: number) => {
        return (
          <ErrorItemDetails
            key={index}
            error={error}
            showDetails={showDetails}
          />
        );
      })}
    </Alert>
  );
};
export default ErrorAlerts;

/**
 * Component displays error details in an accordion.
 * Used in ErrorAlerts component to display the details
 * of each error when showDetails is true.
 */
type ErrorItemDetailsProps = {
  error: ErrorDetails;
  showDetails: boolean;
};

const ErrorItemDetails = ({ error, showDetails }: ErrorItemDetailsProps) => {
  const theme = useTheme();

  const hasStatus = Boolean(error?.status);
  const hasMessage = Boolean(error?.message);
  const hasFullDetails = showDetails && Boolean(error?.fullDetails);

  // Format the full details for display
  // trycatch as stringify can throw (eg: circular reference...)
  const fullDetailsJson = useMemo(() => {
    if (!hasFullDetails) {
      return null;
    }
    try {
      return JSON.stringify(error?.fullDetails, null, 2);
    } catch (err) {
      console.error('[ErrorItemDetails-1]:', err);
      return String(err);
    }
  }, [hasFullDetails, error?.fullDetails]);

  const accordionSx = useMemo(() => {
    return {
      bgcolor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.action.hover}`,
      boxShadow: 'none',
      '& .MuiAccordionSummary-root': {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&.Mui-expanded': {
          minHeight: 48,
          borderBottom: `1px solid ${theme.palette.action.hover}`,
        },
        '& .MuiAccordionSummary-content': {
          m: 0,
          p: 1,
        },
      },
      '& .MuiAccordionDetails-root': {
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 1,
        p: 1,
        m: 0,
      },
      '& .MuiButtonBase-root': {
        m: 0,
        px: 1,
        minHeight: 0,
      },
    };
  }, [theme.palette.action.hover]);

  return (
    <Box
      data-testid="error-details-display"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {hasStatus && (
        <Typography
          data-testid="error-detail-status"
          title="Error Status"
          sx={{ fontSize: 16, fontWeight: 500 }}
        >
          {error?.status}
        </Typography>
      )}

      {hasMessage && (
        <Typography
          data-testid="error-detail-message"
          title="Error Message"
          sx={{ fontSize: 14 }}
        >
          {error?.message}
        </Typography>
      )}

      {hasFullDetails && (
        <Accordion
          data-testid="error-details-accordion"
          sx={accordionSx}
        >
          <AccordionSummary
            data-testid="error-details-accordion-summary"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="error-details-data"
          >
            <Typography
              data-testid="error-details-accordion-summary-text"
              sx={{ fontWeight: 500, fontSize: '14px' }}
            >
              Error Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              id="error-details-data"
              data-testid="error-details-data"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontWeight: 400,
                fontSize: 14,
                fontFamily: 'monospace',
              }}
            >
              {fullDetailsJson}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
